import {FC, useState} from 'react';
import {Box, Button, Chip, Typography} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import {useIntl} from 'react-intl';
import dayjs from 'dayjs';
import {useScreenTier} from 'src/hooks/useScreenTier';
import AMPModal from 'src/components/common/AMPModal';
import {PickCustomPeriodForm} from 'src/components/forms/PickCustomPeriodForm';
import {PickCustomPeriodFormReturnType} from 'src/components/forms/PickCustomPeriodForm/PickCustomPeriodForm.types';
import {filtersSx} from './FiltersBar.sx';

export type EnergyTypeFilter = undefined | 'REGULAR_NETWORK' | 'SOLAR' | 'BATTERY_STORAGE';

interface FiltersBarProps {
    dateFrom: number;
    dateTo: number;
    onDateChange: (from: number, to: number) => void;
    energyType: EnergyTypeFilter;
    onEnergyTypeChange: (value: EnergyTypeFilter) => void;
}

// The three periods the design puts on a phone, in its order: a year, a month, a week. They are named
// rather than recomputed, so a chip asks for exactly the dates its desktop twin asks for.
const PHONE_PRESET_IDS = ['thisYear', 'last30days', 'lastWeek'];

export const FiltersBar: FC<FiltersBarProps> = ({
    dateFrom,
    dateTo,
    onDateChange,
    energyType,
    onEnergyTypeChange,
}) => {
    const intl = useIntl();
    // Below md the same presets become three full-width chips; from md up the bar is untouched.
    const {isCompact} = useScreenTier();
    const [showCalendar, setShowCalendar] = useState(false);

    const energyOptions: Array<{value: EnergyTypeFilter; labelId: string}> = [
        {value: undefined, labelId: 'ecEnergyTypeAll'},
        {value: 'REGULAR_NETWORK', labelId: 'ecEnergyTypeRegular'},
        {value: 'SOLAR', labelId: 'ecEnergyTypeSolar'},
        {value: 'BATTERY_STORAGE', labelId: 'ecEnergyTypeBattery'},
    ];

    const presets = [
        {
            labelId: 'lastWeek',
            from: dayjs.utc().subtract(1, 'week').valueOf(),
            to: dayjs.utc().valueOf(),
        },
        {
            labelId: 'last30days',
            from: dayjs.utc().subtract(1, 'month').valueOf(),
            to: dayjs.utc().valueOf(),
        },
        {
            labelId: 'thisYear',
            from: dayjs.utc().startOf('year').valueOf(),
            to: dayjs.utc().valueOf(),
        },
        {
            labelId: 'all',
            from: dayjs.utc('2020-01-01').valueOf(),
            to: dayjs.utc().valueOf(),
        },
    ];

    const isPresetActive = (from: number, to: number) => {
        return Math.abs(from - dateFrom) < 86400000 && Math.abs(to - dateTo) < 86400000;
    };

    const phonePresets = PHONE_PRESET_IDS
        .map((labelId) => presets.find((p) => p.labelId === labelId))
        .filter((p): p is (typeof presets)[number] => Boolean(p));

    // Whatever the three chips do not cover — "All" today — keeps its place on the line below them,
    // beside the calendar. A period that is unreachable is worse than a period that is a tap further.
    const otherPresets = presets.filter((p) => !PHONE_PRESET_IDS.includes(p.labelId));

    const handleCustomSubmit = () => (values: PickCustomPeriodFormReturnType) => {
        const startLocal = dayjs(values.range.startDate);
        const endLocal = dayjs(values.range.endDate);

        const startDate = dayjs.utc()
            .year(startLocal.year()).month(startLocal.month()).date(startLocal.date())
            .hour(0).minute(0).second(0);
        const endDate = dayjs.utc()
            .year(endLocal.year()).month(endLocal.month()).date(endLocal.date())
            .hour(23).minute(59).second(59);

        onDateChange(startDate.valueOf(), endDate.valueOf());
        setShowCalendar(false);
    };

    // One set of nodes for both widths: the energy filter is the same control on a phone as on a
    // desktop, which is what the design asks for.
    const energyFilters = energyOptions.map((opt) => {
        const active = energyType === opt.value;
        return (
            <Chip
                key={opt.labelId}
                label={intl.formatMessage({id: opt.labelId})}
                sx={active ? filtersSx.chipActive : filtersSx.chip}
                onClick={() => onEnergyTypeChange(opt.value)}
            />
        );
    });

    const periodChip = (preset: (typeof presets)[number]) => (
        <Chip
            key={preset.labelId}
            label={intl.formatMessage({id: preset.labelId})}
            sx={isPresetActive(preset.from, preset.to) ? filtersSx.phoneChipActive : filtersSx.phoneChip}
            onClick={() => onDateChange(preset.from, preset.to)}
        />
    );

    const calendarLabel = `${dayjs(dateFrom).format('DD MMM')} — ${dayjs(dateTo).format('DD MMM YYYY')}`;

    return (
        <>
            {isCompact ? (
                <Box sx={filtersSx.phoneRoot}>
                    <Box sx={filtersSx.phonePresetRow}>
                        {phonePresets.map(periodChip)}
                    </Box>

                    <Box sx={filtersSx.phoneSecondaryRow}>
                        {otherPresets.map(periodChip)}
                        <Button
                            startIcon={<CalendarTodayOutlinedIcon sx={{fontSize: 16}} />}
                            sx={filtersSx.phoneDateBtn}
                            onClick={() => setShowCalendar(true)}
                        >
                            {calendarLabel}
                        </Button>
                    </Box>

                    <Box sx={filtersSx.phoneEnergyRow}>
                        <Typography sx={filtersSx.label}>Energy:</Typography>
                        {energyFilters}
                    </Box>
                </Box>
            ) : (
                <Box sx={filtersSx.root}>
                    <Box sx={filtersSx.group}>
                        {presets.map((p) => {
                            const active = isPresetActive(p.from, p.to);
                            return (
                                <Chip
                                    key={p.labelId}
                                    label={intl.formatMessage({id: p.labelId})}
                                    sx={active ? filtersSx.chipActive : filtersSx.chip}
                                    onClick={() => onDateChange(p.from, p.to)}
                                />
                            );
                        })}
                        <Button
                            startIcon={<CalendarTodayOutlinedIcon sx={{fontSize: 16}} />}
                            sx={filtersSx.dateBtn}
                            onClick={() => setShowCalendar(true)}
                        >
                            {dayjs(dateFrom).format('DD MMM')} — {dayjs(dateTo).format('DD MMM YYYY')}
                        </Button>
                    </Box>

                    <Box sx={filtersSx.divider} />

                    <Box sx={filtersSx.group}>
                        <Typography sx={filtersSx.label}>Energy:</Typography>
                        {energyFilters}
                    </Box>
                </Box>
            )}

            <AMPModal
                handleClose={() => setShowCalendar(false)}
                open={showCalendar}
                title={intl.formatMessage({id: 'customPeriod'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <PickCustomPeriodForm
                    handleClose={() => setShowCalendar(false)}
                    handleSubmit={handleCustomSubmit()}
                    initialValues={{
                        range: {
                            startDate: new Date(dateFrom),
                            endDate: new Date(dateTo),
                            key: 'selection',
                        },
                    }}
                />
            </AMPModal>
        </>
    );
};
